import i18n from "src/locales";
import {
  getPromoCodeState,
  isPromoCodeFromPriorSession,
  resetPromoCodeAppliedFromPriorSession,
  setPromoCode,
} from "@utils/redux/slices/promoCode";
import saveToBraze from "../../brazeUploader";
import { shift_days, toISO } from "../../datetime";
import { queryClient, send } from "../../fetch";
import { getDefaultTrialFee } from "../../plans";
import {
  CheckoutState,
  paymentTypes,
  updateCheckoutState,
} from "../../redux/slices/checkout";
import { Plan } from "../../redux/slices/plans";
import {
  RecommendedPlanState,
  setTrialFee,
  updateRecommendedPlan,
} from "../../redux/slices/recommendedPlan";
import getStore, {
  AppDispatch,
  CoreReduxState,
  GetAppState,
} from "../../redux/store";
import { captureException } from "../../error";
import { prepareGrowthAPIParameters } from "../../services/api-params";
import { trackPurchase } from "../../services/ConversionTracker";
import { errorConstants } from "../../services/PurchaseErrorMessages";
import {
  isCA,
  isEnLanguage,
  isIntl,
  isInApp,
  isIOS,
  isHM,
} from "../../userSegment";
import {
  isCollapsedPaymentEligible,
  isInAppWebPurchaseEligible,
  acceptedCoursePackOffer,
  isPlanSelectionEligible,
  isDevMode,
} from "../../userSegment/features";
import { trackEmailChanged, trackEvent } from "../tracker";
import { getReferralCode, submitReferralEmails } from "@utils/api/referrals";
import { decoratePurchaseRequestWithProductCatalog } from "@utils/api/purchase/productPlan";
import { SurveyAnswersState } from "@utils/redux/slices/surveyAnswers";
import {
  applyFreeMonthsOffer,
  isPlanEligibleForFreeMonthsOffer,
} from "@utils/freeMonthsOffer";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { prepareDataForBackend } from "src/utils/pace";
import {
  getCountryCode,
  getLanguage,
  getLocale,
  getMeristemContext,
} from "../../meristemContext";
import { createAppAsyncThunk } from "../../redux/async-thunk";
import { getSalesTax } from "src/utils/api/addons/salesTax";
import { updatePaymentAddon } from "src/utils/redux/slices/paymentAddon";
import { loadWinbackInfo } from "src/utils/redux/slices/winbackInfo";
import { getECTExperiments } from "src/utils/experiment";
import { JsonObject } from "type-fest";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { setOptOutEmail } from "src/utils/consent/opt-out";
import { isTelehealthPlanId } from "src/components/refactored-survey/question-sets/insurance-survey-questions/isTelehealthPlanId";
import { getPaymentMethod } from "src/checkout/tracking";

const PROMO_CODE_EXPIRY_DURATION = 60 * 60 * 24 * 2; // 48 hours
const PROMO_CODE_EXPIRY_DURATION_DEV = 30; // 30 sec for dev mode
const PROMO_CODE_VALIDATION_URL = "/discounts/api/v1/validate/";
const SALES_TAX_CALCULATION_URL = "/api/tax";

export type SalesTaxAPIResponse = {
  is_tax_inclusive: boolean;
  plan: Plan;
  sales_tax_amount: number;
  sales_tax_state: string;
  sales_tax_rate: number;
};

/**
 * Makes a call to the backend to compute the discounted plan if the promo code is valid. Discounted plan will be returned and the promo code redux slice will be updated.
 * If promo code is not valid, promo code redux slice will be updated with an error state.
 * @param plan The type of `plan` needs to be `RecommendedPlanState` because in some places we pass the recommendedPlan.
 * @param promoCode During this call we also validate the promo code and save it into the redux store if it is valid.
 * @param freeMonths The number of free months, in case we redeem a free months offer
 * @returns The plan with the prices discounted based on the promo code.
 */
export function applyPromoCode(
  plan: RecommendedPlanState,
  promoCode: string,
  freeMonths?: number
) {
  return async (dispatch: AppDispatch, getState: GetAppState) => {
    const { serverContext, surveyAnswers, userData } = getState();

    const promoCodeFromPriorSession = isPromoCodeFromPriorSession(promoCode);
    if (promoCodeFromPriorSession) {
      resetPromoCodeAppliedFromPriorSession();
    }
    const task = trackTask("RedeemingPromoCode", {
      promoCode,
      promoCodeFromPriorSession,
      freeMonths,
    });
    try {
      // MP-Rename: Legacy events to remove
      trackEvent("RedeemingPromoCode", {
        promoCode,
        freeMonths,
      });
      task.start();

      const params: JsonObject = {
        noom_plan_id: plan.noom_plan_id,
        code: promoCode,
        trialFee: plan.trial_fee || getDefaultTrialFee(plan),
        forced_country_code: getMeristemContext().is_forced_country_code
          ? getCountryCode()
          : undefined,
        // NOTE(guorui): for a normal user, surveyAnswers.email will contain the user's email
        // and serverContext.email will be null.
        // serverContext.email is populated when the user deeplinks to the payment page
        // with the email param set
        // userData.email is populated when the email param is not set on a deeplink, and the
        // user fills in an email on the Name and Email form preceding the payment page
        email: serverContext.email || surveyAnswers.email || userData.email,
      };
      if (isPlanSelectionEligible() && isEnLanguage()) {
        params.promoCodeExpiryDurationSec = isDevMode()
          ? PROMO_CODE_EXPIRY_DURATION_DEV
          : PROMO_CODE_EXPIRY_DURATION;
      }

      // NOTE(patrick): For legacy reasons this endpoint sends data as x-www-form-urlencoded
      // but receives responses from the django backend as JSON.
      // Convert our params object into a URLSearchParam format to send to the server.
      // In the future, this can be replaced with JSON.stringify.
      const paramsPayload = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value != null) {
          paramsPayload.append(key, `${value}`);
        }
      });

      const response: Plan = await send(
        "POST",
        PROMO_CODE_VALIDATION_URL,
        paramsPayload,
        {
          params: prepareGrowthAPIParameters(),
        }
      );

      if (response.formatted_promo_discount_amount === "0") {
        const toThrow: any = new Error("checkout:errorPromo");
        toThrow.i18nMessage = "checkout:errorPromo";
        throw toThrow;
      }

      const updatedPlan: Partial<Plan> = {
        ...plan,
        price: response.price,
        monthly_price: response.monthly_price,
        weekly_price: response.weekly_price,
        discounted_plan_price: response.discounted_plan_price,
        discount_info: response.discount_info,
        // NOTE(patrick)[2020/04/14]: The batch symbol can be modified by the server.
        // It is most likely because the curriculum is changed from HW to VIP in the
        // case the promo code is for a VIP user.
        // NOTE(patrick)[2020/04/14]: We get the batch_symbol in recommendedPlan but save it
        // as `curriculum` and pass it back to the payment endpoint as `curriculum` in
        // the plan. `batch_symbol` will still be set in recommendedPlan but it's not
        // to be confused with `curriculum`.
        ...(response.batch_symbol && { curriculum: response.batch_symbol }),
        // Honor promo codes that provide free trials.
        ...(response?.trial_fee === 0 && {
          trial_fee: 0,
          userSelectedTrialFee: 0,
        }),
      };

      let promoCodeExpirationDate = null;
      if (response.promo_code_expiration_date) {
        promoCodeExpirationDate = new Date(
          `${response.promo_code_expiration_date}Z`
        ).toISOString();
      }

      dispatch(
        setPromoCode({
          promoCodeError: "",
          promoCodeApplied: promoCode,
          promoDiscountAmount: response.promo_discount_amount,
          // if response.discount_info.discountType === PERCENT then this will be populated, otherwise 0
          promoDiscountPercentage: response.discount_info?.discountPercent,
          promoCodeIsVip: response.is_vip,
          promoCodeExpirationDate,
          promoCodeFreeMonths: freeMonths,
        })
      );

      task.success({
        promoCodeExpirationDate,
        promoCodeIsVip: response.is_vip,
      });

      const discountType =
        response.discount_info?.discountType === "PERCENT"
          ? "percent"
          : "fixed_amount";
      // MP-Rename: Legacy events to remove
      trackEvent("PromoCodeRedeemed", {
        promoCode,
        promoCodeExpirationDate,
        offerType: freeMonths ? "free_months" : discountType,
        discountPercentage: response.discount_info?.discountPercent,
        discountFixedAmount: response.discount_info?.discountAmount,
        freeMonths,
      });

      if (response.is_vip) {
        trackEvent("RedeemedPromoCodeVIP", {
          promoCode,
        });
      }

      return updatedPlan;
    } catch (err: any) {
      const { response, responseData } = err;
      let promoCodeError = responseData?.message || err;
      if (response?.status === 406) {
        trackEvent("InvalidPromoCode", { promoCode });
        promoCodeError = i18n.t("checkout:invalidCode");
      } else if (response?.status === 410) {
        trackEvent("PromoCodeRedemptionBlocked", {
          promoCode,
          promoCodeExpirationDate: response?.data?.promo_code_expiration_date,
        });
        promoCodeError = i18n.t("checkout:expiredCode");
      } else if (err.i18nMessage) {
        promoCodeError = i18n.t(err.i18nMessage);
      } else {
        promoCodeError = err.toString();
      }
      dispatch(
        setPromoCode({
          promoCodeApplied: "",
          promoDiscountAmount: 0,
          promoDiscountPercentage: 0,
          promoCodeIsVip: false,
          promoCodeFreeMonths: null,
          promoCodeError,
        })
      );
      trackEvent("PromoCodeRedemptionFailed", { promoCode });
      task.failed(err);
      return null;
    }
  };
}

/**
 * Applies the promo code to the recommended plan.
 * Side effects include: updating promo code state, recommended plan state and recomputing sales tax.
 */
export function applyPromoCodeToRecommendedPlan(promoCode: string) {
  return async (dispatch: AppDispatch, getState: GetAppState) => {
    const { checkout, recommendedPlan } = getState();

    let plan: Partial<Plan> = await dispatch(
      applyPromoCode(recommendedPlan, promoCode)
    );

    if (isPlanEligibleForFreeMonthsOffer(plan)) {
      plan = await dispatch(applyFreeMonthsOffer(plan, promoCode));
    }

    if (!plan) {
      // if plan is null an exception occurred inside applyPromoCode or when trying to redeem the free months offer
      return null;
    }

    const shouldDiscountSelectedPlan =
      !(isPlanSelectionEligible() && plan?.billing_cycle_in_months === 1) ||
      plan.is_vip;
    if (shouldDiscountSelectedPlan) {
      dispatch(updateRecommendedPlan(plan));

      // Honor promo codes that provide free trials.
      // Reason behind this is that we need to explicitly update the trial fee, otherwise
      // it would default to the previous value
      if (plan.trial_fee === 0) {
        dispatch(setTrialFee(0));
      }
    }

    // NOTE(Rose): In the case that promocode is entered after zipcode, the disclaimer and order breakdown don't update
    // with the right totals. Forcing rerender by updating state or using this.forceUpdate() don't seem to work, as the
    // rendering seems tied to calculateRecommendedPlanSalesTax().
    if (checkout.billingAddress?.zipcode) {
      return dispatch(calculateRecommendedPlanSalesTax());
    }
    return null;
  };
}

export function calculateAddonSalesTax() {
  return async (dispatch: AppDispatch, getState: GetAppState) => {
    const {
      checkout,
      userData,
      recommendedPlan,
      serverContext,
      surveyAnswers,
      paymentAddon,
    } = getState();

    const { billingAddress } = checkout;

    const salesTaxParams = {
      email: userData.email || serverContext.email || surveyAnswers.email,
      currency: recommendedPlan.currency,
      user_id: userData.user_id,
      productIds: [paymentAddon.addon_product_id],
      billing_address: {
        ...billingAddress,
        zipcode: billingAddress.zipcode.split("-")[0],
      },
    };

    return getSalesTax(salesTaxParams)
      .then((response) => {
        dispatch(
          updatePaymentAddon({
            sales_tax_amount: response.sales_tax_amount,
            sales_tax_rate: response.wavg_tax_rate,
            is_tax_inclusive: response.is_tax_inclusive,
          })
        );
      })
      .catch((err: any) => {
        captureException(err);
      });
  };
}

/**
 * Given a US zipcode or CA postal code and region, fetch calculated sales tax and then
 * update the plan stored in the state and salesTaxAmount.
 */
export function calculateRecommendedPlanSalesTax() {
  return async (dispatch: AppDispatch, getState: GetAppState) => {
    const { checkout, recommendedPlan, serverContext, surveyAnswers } =
      getState();
    const promoCode = getPromoCodeState();

    const { billingAddress } = checkout;

    // Both region and zipcode are required to calculate sales tax for Candian users
    // so in the case the zipcode is provided but an empty value for region is set for billing address,
    // exit early.
    if (!billingAddress?.zipcode || (isCA() && !billingAddress.region)) {
      return Promise.resolve({});
    }

    const basePayload = {
      country: getCountryCode(),
      continentCode: getMeristemContext().continent_code,
      zipcode: billingAddress.zipcode,
      noomPlanId: recommendedPlan.noom_plan_id,
      promoCode: promoCode.promoCodeApplied,
      trialFee: recommendedPlan.trial_fee,
      billingAddress: {
        ...billingAddress,
        zipcode: billingAddress.zipcode.split("-")[0],
      },
      // NOTE(guorui): for a normal user, surveyAnswers.email will contain the user's email
      // and serverContext.email will be null.
      // serverContext.email is populated when the user deeplinks to the payment page
      // with the email param set
      email: serverContext.email || surveyAnswers.email,
    };
    // EN-INTL surface includes information about tax-inclusive pricing
    let payload;
    if (isIntl()) {
      payload = { ...basePayload, returnTaxForTaxInclusivePlans: true };
    } else {
      payload = basePayload;
    }

    return send("POST", SALES_TAX_CALCULATION_URL, payload, {
      params: prepareGrowthAPIParameters(),
    })
      .then((data: SalesTaxAPIResponse) => {
        const { plan } = data;
        // hacky temp fix: tax endpoint returns different payment source
        // clearing up the payment form
        delete plan.payment_source;
        dispatch(updateRecommendedPlan(plan));
        dispatch(
          updateCheckoutState({
            salesTaxAmount: data.sales_tax_amount,
            salesTaxRate: data.sales_tax_rate || 0,
            salesTaxState: data.sales_tax_state,
            isTaxInclusive: data.is_tax_inclusive,
          })
        );
        return data;
      })
      .catch((err: any) => {
        captureException(err);
      });
  };
}

const PURCHASE_URL = "/api/payment/v2/purchase_program/";

export interface PurchaseProgramResponse {
  eltv_13_months: number;
  email: string;
  erev_9_months: number;
  name: string;
  payment_cc_last_4: string;
  payment_cc_type: string;
  subscriptionId: string;
  user_id: string;
  upid: string;
}

export const requestPurchase = createAppAsyncThunk(
  "checkoutPurchase/request",
  async (
    params: {
      curriculum?: string;
      vipCurriculum?: string;
      paymentNonce: string;
      recaptchaToken: string;
    },
    thunkApi
  ) => {
    const coreState = thunkApi.getState();
    const { recommendedPlan } = coreState;

    const purchaseRequest = await preparePurchaseRequest({
      coreState,
      curriculum: params.curriculum,
      vipCurriculum: params.vipCurriculum,
      paymentMethodNonce: params.paymentNonce,
      recaptchaToken: params.recaptchaToken || null,
    });

    const task = trackTask("StartSubscription", {
      paymentMethod: getPaymentMethod(coreState.checkout, coreState.promoCode),
      planId: recommendedPlan.braintree_id,
    });
    let response: PurchaseProgramResponse;
    try {
      task.start();
      response = await send("POST", PURCHASE_URL, purchaseRequest, {
        params: prepareGrowthAPIParameters(),
      });
      task.success();
    } catch (e) {
      const errorData = e.responseData;
      let errorType = errorData?.errorType || errorConstants.errorUnknown;
      // NOTE(sumin): Quick hack to be backwards compatible but show sales tax location error.
      if (errorData?.errorCode === errorConstants.errorSalesTaxLocation) {
        errorType = errorConstants.errorSalesTaxLocation;
      }

      task.failed(e, {
        error: errorType,
        result: errorType,
        request: purchaseRequest,
        errorData,
      });
      throw e;
    }
    // After successful signup, track events
    try {
      await trackPurchaseSuccess(
        coreState,
        response,
        purchaseRequest.email,
        purchaseRequest.name
      );
    } catch (e) {
      // Trackers failed, but let the user continue; just report this error.
      captureException(e);
    }
    return {
      response,
      request: purchaseRequest,
    };
  }
);

export type PaymentMethodType = "PayPal" | "SEPA" | "CreditCard";
export function getPaymentMethodType(
  checkout: CheckoutState
): PaymentMethodType {
  switch (checkout.paymentType) {
    case paymentTypes.PAYPAL:
      return "PayPal";
    case paymentTypes.SEPA:
      return "SEPA";
    case paymentTypes.CREDIT_CARD:
      return "CreditCard";
    default:
      throw new Error(`Unhandled payment type: ${checkout.paymentType}`);
  }
}

async function preparePurchaseRequest({
  coreState,
  curriculum,
  vipCurriculum,
  paymentMethodNonce,
  recaptchaToken,
}: {
  coreState: CoreReduxState;
  curriculum: string;
  vipCurriculum: string;
  paymentMethodNonce: string;
  recaptchaToken: string;
}) {
  // Extract necessary state from Redux
  const { checkout, paymentEnrollmentForm, userData, recommendedPlan } =
    coreState;
  const promoCode = getPromoCodeState(coreState);

  // Only use info from paymentEnrollmentForm slice if eligible for collapsed payment enrollment UI
  // and not VIP
  const name =
    isCollapsedPaymentEligible() && !promoCode.promoCodeIsVip
      ? paymentEnrollmentForm.enrollmentInfo.name
      : userData.name;
  const email =
    isCollapsedPaymentEligible() && !promoCode.promoCodeIsVip
      ? paymentEnrollmentForm.enrollmentInfo.email.toLowerCase()
      : userData.email.toLowerCase();
  const paymentMethod = getPaymentMethodType(checkout);
  // NOTE(patrick): growthProgramStartDateISO is used by braze and is required to be ISO8601.
  const growthProgramStartDateISO = toISO();
  const expirationDate = shift_days(recommendedPlan.trial_duration, new Date());
  const growthTrialEndDate = expirationDate.toLocaleDateString(getLocale());
  const growthTrialEndDateISO = toISO(expirationDate);

  let paymentProcessor;
  if (checkout.paymentType === "PAYPAL") {
    paymentProcessor = "BRAINTREE";
  } else if (checkout.walletPay || checkout.paymentType === paymentTypes.SEPA) {
    paymentProcessor = "STRIPE";
  } else {
    paymentProcessor = recommendedPlan.payment_source;
  }

  // Assemble the purchase request.
  let purchaseRequest = {
    // Only use info from paymentEnrollmentForm if eligible for collapsed payment enrollment UI and
    // not VIP
    name,
    email,
    countryCode: getCountryCode(),
    continentCode: getMeristemContext().continent_code,
    noomPlanId: recommendedPlan.noom_plan_id,
    braintreeId: recommendedPlan.braintree_id,
    merchantAccount: recommendedPlan.merchant_account,
    language: getLanguage(),

    curriculum:
      recommendedPlan.curriculum ||
      (promoCode.promoCodeIsVip && vipCurriculum) ||
      curriculum,
    inAppPurchase: isInAppWebPurchaseEligible(coreState),
    courseAddOns: await getCourseAddOnsToPurchase(),

    paymentMethodType: paymentMethod,
    plan: recommendedPlan,
    paymentProcessor,
    zipcode: checkout.billingAddress?.zipcode,
    growthProgramStartDateISO,
    growthTrialEndDate,
    growthTrialEndDateISO,

    billingAddress: {
      ...(checkout.billingAddress || {
        country: getCountryCode(),
      }),
      name: checkout.billingName,
    },
    experiments: getECTExperiments(),
    trialFee: undefined,
    paymentMethodNonce,
    recaptchaToken,

    subscriptionSaleItemId: undefined,
    oneSourceTaxCode: recommendedPlan?.one_source_tax_code,
    trialSaleItemId: undefined,

    ...(promoCode.promoCodeApplied && {
      promoCodeApplied: promoCode.promoCodeApplied,
      promoDiscountAmount: promoCode.promoDiscountAmount,
      promoCodeIsVip: promoCode.promoCodeIsVip,
    }),

    ...(promoCode.promoCodeApplied &&
      promoCode.promoCodeFreeMonths && {
        swappedPlanDuration:
          recommendedPlan.billing_cycle_in_months -
          promoCode.promoCodeFreeMonths,
      }),

    shouldEnroll: undefined,
    growthPaymentPayPal: undefined,
  };

  if (!isHM()) {
    const surveyAnswers = getSurveyAnswers(coreState);
    const weightLossData = prepareDataForBackend(surveyAnswers);

    if (weightLossData) {
      // Need to rename weightInKg field to startWeightInKg for setActiveGoalUsingPOST endpoint
      const { weightInKg, ...otherProps } = weightLossData;
      const newWeightLossData = { startWeightInKg: weightInKg, ...otherProps };
      (purchaseRequest as any).weightLossData = newWeightLossData;
    }
  }

  purchaseRequest = decoratePurchaseRequestWithProductCatalog(
    purchaseRequest,
    recommendedPlan
  );

  purchaseRequest = decoratePurchaseRequestWithUrlParams(purchaseRequest);

  // Pass in shouldEnroll only if eligible for collapsed payment enrollment UI and not VIP
  if (isCollapsedPaymentEligible(coreState) && !promoCode.promoCodeIsVip) {
    purchaseRequest.shouldEnroll = true;
  }

  if (checkout.paymentType === paymentTypes.PAYPAL) {
    purchaseRequest.growthPaymentPayPal = checkout.paypalAccount;
  }
  return purchaseRequest;
}

async function getCourseAddOnsToPurchase() {
  const store = getStore();
  const { paymentAddon, userData, paymentEnrollmentForm } = store.getState();
  if (userData.email !== paymentEnrollmentForm.enrollmentInfo.email) {
    await store.dispatch(loadWinbackInfo(true));
  }
  return acceptedCoursePackOffer() ? [paymentAddon.addon_product_id] : [];
}

function decoratePurchaseRequestWithUrlParams(
  purchaseRequest: any
): typeof purchaseRequest {
  const urlParams = new URLSearchParams(window.location.search);

  const newPurchaseRequest = { ...purchaseRequest };
  if (urlParams.has("tkbl_cvuuid")) {
    newPurchaseRequest.talkableCvuuid = urlParams.get("tkbl_cvuuid");
  }

  return newPurchaseRequest;
}

async function trackPurchaseSuccess(
  coreState: CoreReduxState,
  response: PurchaseProgramResponse,
  email: string,
  name: string
) {
  const { recommendedPlan } = coreState;
  // Track the signup.
  const trackerPayload = {
    planId: recommendedPlan.braintree_id,
    planDuration: recommendedPlan.billing_cycle_in_months,
    planCurrency: recommendedPlan.currency,
    planOriginalPrice: recommendedPlan.regular_price,
    planPrice: recommendedPlan.price,
    hasTrial: recommendedPlan.has_trial,
    paymentMethod: getPaymentMethod(coreState.checkout, coreState.promoCode),
    paymentCCLast4: response.payment_cc_last_4,
    paymentCCType: response.payment_cc_type,
    trialFeeAmount: recommendedPlan.trial_fee || 0,
    isTelehealthPlan: isTelehealthPlanId(recommendedPlan.braintree_id),
    ...response,
  };
  trackEvent("OnSignedUp", trackerPayload);
  trackEvent("CoursePackOfferPurchased", {
    answer: acceptedCoursePackOffer() ? "yes" : "no",
  });
  trackEmailChanged("purchase", email, coreState);
  trackEvent("PlanLengthSelected", {
    planLength: recommendedPlan.isSwitchedPlan ? "suggested" : "assigned",
  });
  setOptOutEmail(email);

  if (isInApp(coreState) && isIOS()) {
    await saveToBraze({ event: { event_name: "ios_app_signup" } });
  }

  // Fire tracking pixels
  const surveyAnswers = getSurveyAnswers(coreState);

  await trackReferralEmails(surveyAnswers, email, name);

  trackPurchase(email, recommendedPlan, surveyAnswers, response);
}

async function trackReferralEmails(
  surveyAnswers: SurveyAnswersState,
  email: string,
  name: string
) {
  if (surveyAnswers.inviteFriendsFamily?.length > 0) {
    // const referralCode = await getReferralCode();
    const referralCode = await queryClient.fetchQuery(
      ["referralCode"],
      getReferralCode
    );
    await submitReferralEmails(
      email,
      surveyAnswers.inviteFriendsFamily,
      "buyflow",
      referralCode,
      { signedUp: true, name }
    );
  }
}
